import React, { useMemo, useState } from "react";
import { EmptyState } from "../../../../components/ui/PageBits";
import { fmtTime, getSlots, slotCfg } from "../../bookingService";
import { BookingStatusPill } from "./ShopBits";
import BookingEditModal from "./BookingEditModal";

const NEXT = ["pending", "confirmed", "completed", "cancelled", "noshow"];

/** All of the shop's bookings: edit, confirm, complete, reschedule, cancel. */
export default function ShopBookings({ shop, resourceTypes, resources, bookings, onStatus, onReschedule, onEdit }) {
    const [typeId, setTypeId] = useState("all");
    const [status, setStatus] = useState("all");
    const [q, setQ] = useState("");
    const [moving, setMoving] = useState(null); // booking being rescheduled
    const [moveDate, setMoveDate] = useState("");
    const [moveSlot, setMoveSlot] = useState(null);
    const [editing, setEditing] = useState(null); // booking being edited

    const myTypes = resourceTypes.filter((t) => t.ownerId === shop.id);
    const resName = (id) => resources.find((r) => r.id === id)?.name || "(deleted)";
    const typeName = (id) => resourceTypes.find((t) => t.id === id)?.name || "—";

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return bookings
            .filter((b) => b.ownerId === shop.id)
            .filter((b) => (typeId === "all" ? true : b.typeId === typeId))
            .filter((b) => (status === "all" ? true : b.status === status))
            .filter((b) => {
                if (!needle) return true;
                return [b.id, b.customer, b.email, b.phone].join(" ").toLowerCase().includes(needle);
            })
            .sort((a, b) => `${b.date} ${b.start}`.localeCompare(`${a.date} ${a.start}`));
    }, [bookings, shop.id, typeId, status, q]);

    const moveSlots = useMemo(() => {
        if (!moving) return [];
        const type = resourceTypes.find((t) => t.id === moving.typeId);
        return getSlots(bookings, moving.resourceId, moveDate || moving.date, slotCfg(type));
    }, [moving, moveDate, bookings, resourceTypes]);

    const openMove = (b) => {
        setMoving(b);
        setMoveDate(b.date);
        setMoveSlot(null);
    };

    return (
        <div className="space-y-4">
            <div className="hk-card">
                <div className="flex flex-col md:flex-row gap-2">
                    <input
                        className="hk-input md:flex-1"
                        placeholder="Search ref, customer, email, phone…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    <div className="flex gap-2">
                        <select className="hk-select" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
                            <option value="all">All calendars</option>
                            {myTypes.map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                        <select className="hk-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="all">All statuses</option>
                            {NEXT.map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="hk-table-wrap">
                {filtered.length === 0 ? (
                    <EmptyState icon="📅" title="No bookings match" hint="Try different filters — or share your booking link to get some." />
                ) : (
                    <table className="hk-table">
                        <thead>
                            <tr>
                                <th>Ref</th>
                                <th>Customer</th>
                                <th>Calendar · resource</th>
                                <th>When</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((b) => (
                                <tr key={b.id}>
                                    <td className="hk-mono text-xs">{b.id}</td>
                                    <td>
                                        <div className="font-bold">{b.customer}</div>
                                        <div className="text-xs text-[#64748b]">{b.email}{b.phone ? ` · ${b.phone}` : ""}</div>
                                        {b.notes && <div className="text-xs text-[#64748b] italic">“{b.notes}”</div>}
                                    </td>
                                    <td>
                                        <div className="text-xs font-bold">{typeName(b.typeId)}</div>
                                        <div className="text-xs text-[#64748b]">{resName(b.resourceId)}</div>
                                    </td>
                                    <td className="whitespace-nowrap text-xs hk-mono">{b.date}<br />{fmtTime(b.start)} – {fmtTime(b.end)}</td>
                                    <td><BookingStatusPill status={b.status} /></td>
                                    <td className="text-right whitespace-nowrap">
                                        <div className="flex gap-1 justify-end flex-wrap items-center">
                                            <select
                                                className="hk-select hk-mono"
                                                style={{ width: "auto", textTransform: "capitalize" }}
                                                value={b.status}
                                                title="Change booking status"
                                                onChange={(e) => onStatus(b.id, e.target.value)}
                                            >
                                                {NEXT.map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                            <button className="hk-btn hk-btn-outline hk-btn-sm" onClick={() => setEditing(b)}>
                                                Edit
                                            </button>
                                            {(b.status === "confirmed" || b.status === "pending") && (
                                                <button className="hk-btn hk-btn-outline hk-btn-sm" onClick={() => openMove(b)}>
                                                    Move
                                                </button>
                                            )}
                                            {b.status !== "cancelled" && (
                                                <button
                                                    className="hk-btn hk-btn-outline hk-btn-sm"
                                                    style={{ color: "var(--hk-danger)" }}
                                                    onClick={() => {
                                                        if (window.confirm(`Cancel booking ${b.id} (${b.customer})?`)) onStatus(b.id, "cancelled");
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {moving && (
                <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(15,23,42,.45)" }}>
                    <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content" style={{ borderRadius: 14, border: "none" }}>
                            <div className="modal-header">
                                <h5 className="modal-title hk-display font-bold">Move {moving.id}</h5>
                                <button type="button" className="btn-close" onClick={() => setMoving(null)}></button>
                            </div>
                            <div className="modal-body">
                                <p className="text-sm text-[#64748b]">
                                    {moving.customer} · {resName(moving.resourceId)} · currently {moving.date} {fmtTime(moving.start)}
                                </p>
                                <label className="hk-label">New date</label>
                                <input
                                    type="date"
                                    className="hk-input mb-3"
                                    value={moveDate}
                                    onChange={(e) => { setMoveDate(e.target.value); setMoveSlot(null); }}
                                />
                                <label className="hk-label">New time</label>
                                <div className="flex flex-wrap gap-2">
                                    {moveSlots.map((s) => (
                                        <button
                                            key={s.start}
                                            disabled={!s.available}
                                            onClick={() => setMoveSlot(s)}
                                            className={`hk-btn hk-btn-sm ${moveSlot?.start === s.start ? "hk-btn-primary" : "hk-btn-outline"}`}
                                            style={!s.available && moveSlot?.start !== s.start ? { opacity: 0.4 } : undefined}
                                        >
                                            {fmtTime(s.start)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="hk-btn hk-btn-outline hk-btn-sm" onClick={() => setMoving(null)}>Close</button>
                                <button
                                    className="hk-btn hk-btn-primary hk-btn-sm"
                                    disabled={!moveSlot}
                                    onClick={() => {
                                        onReschedule(moving.id, { date: moveDate, start: moveSlot.start, end: moveSlot.end });
                                        setMoving(null);
                                    }}
                                >
                                    Confirm new time
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {editing && (
                <BookingEditModal
                    booking={editing}
                    resources={resources}
                    bookings={bookings}
                    onClose={() => setEditing(null)}
                    onSave={(patch) => {
                        onEdit(editing.id, patch);
                        setEditing(null);
                    }}
                />
            )}
        </div>
    );
}
